import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Stack, Text } from '@mantine/core';
import { AxiosError } from 'axios';
import cookie from 'js-cookie';

import AuthFormCard from '../../components/AuthFormCard';
import { LogInForm } from '../../components/Forms';

import { useNotificationSystem } from '../../hooks';
import { useLogIn } from '../../hooks/mutations';

import { useLogInFormContext } from '../../contexts/forms';

import { useTwoFactorLoginStore } from '../../stores';
import { postLoginRedirector } from '../../utils';

export default function LogInPage() {
  const navigate = useNavigate();
  const form = useLogInFormContext();
  const [searchParams] = useSearchParams();
  const { mutate: logIn, status: logInStatus } = useLogIn();
  const { createNotification } = useNotificationSystem();

  const activationStatus = searchParams.get('activation');
  const nextRoute = searchParams.get('next') || '/home';

  const setUsername = useTwoFactorLoginStore(state => state.setUsername);
  const setPassword = useTwoFactorLoginStore(state => state.setPassword);

  const handleLogIn = () => {
    logIn(form.values, {
      onSuccess: async response => {
        const { status } = response;

        if (status === 412) {
          setUsername(form.values.username);
          setPassword(form.values.password);
          return navigate('/login/two-factor');
        }

        const { token } = response.data;
        cookie.set('ATK', token);

        return postLoginRedirector({ token, route: nextRoute });
      },
      onError: error => {
        if (error instanceof AxiosError && error.response) {
          const { status, data } = error.response;

          if (
            status === 400 &&
            data?.non_field_errors &&
            data?.non_field_errors[0] ===
              'Unable to log in with provided credentials.'
          ) {
            createNotification('error', {
              title: 'Invalid credentials',
              message:
                'The email or password you have entered is incorrect. Please double-check your credentials and try again.',
            });
          } else {
            createNotification('error', {
              title: 'Error',
              message: 'Something went wrong. Please try again.',
            });
          }
        }
      },
    });
  };

  return (
    <AuthFormCard showSignUpPrompt title="Log in to your account">
      <Stack>
        {activationStatus ? (
          <Alert
            color={activationStatus === 'success' ? 'kt-blue' : 'red'}
            variant="light"
            title={
              activationStatus === 'success'
                ? 'Account activated'
                : 'Activation failed'
            }
          >
            {activationStatus === 'success' ? (
              <Text component="span">
                You may now log in to your account using the email and password
                you provided during registration.
              </Text>
            ) : (
              <Text component="span">
                Your account could not be activated. Please check the activation
                link you received in your email and try again.
              </Text>
            )}
          </Alert>
        ) : null}
        <LogInForm
          isSubmitting={logInStatus === 'loading'}
          onSubmit={handleLogIn}
        />
      </Stack>
    </AuthFormCard>
  );
}
